import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, DatePicker, Checkbox, Select, Input, Button, Form, message, Drawer, Layout, Tabs, Divider, Space, Typography, Tooltip, Spin, Modal } from 'antd';
import { FilePdfOutlined, CheckCircleFilled, CheckOutlined, CloseOutlined, CloudOutlined, SunOutlined, ThunderboltOutlined, CloudFilled, EyeOutlined, StopOutlined, LoadingOutlined, CheckCircleOutlined } from '@ant-design/icons';
import DSRSignaturePad from './DSRSignaturePad';
import { connect } from 'react-redux';
import {
  createDsrLog,
  submitDsrLog,
  unSubmitDsrLog,
  updateDsrLogSections,
  generateFlashReport,
} from '../../redux/dsr/action';
import { get } from 'lodash';
import moment from 'moment';
import { getJwtToken } from '../../services/auth';

const { Option } = Select;
const { TextArea } = Input;
const { Content, Footer } = Layout;
const { TabPane } = Tabs;

const weatherOptions = [
    { value: '1', label: 'Sunny', icon: <SunOutlined style={{ color: '#faad14' }} /> },
    { value: '2', label: 'Fair', icon: <SunOutlined style={{ color: '#52c41a' }} /> },
    { value: '3', label: 'Partly Cloudy', icon: <CloudOutlined style={{ color: '#8c8c8c' }} /> },
    { value: '4', label: 'Mostly Cloudy', icon: <CloudFilled style={{ color: '#595959' }} /> },
    { value: '5', label: 'Cloudy', icon: <CloudFilled style={{ color: '#434343' }} /> },
    { value: '6', label: 'Haze/Fog', icon: <EyeOutlined style={{ color: '#bfbfbf' }} /> },
    { value: '7', label: 'Rainy', icon: <CloudFilled style={{ color: '#1890ff' }} /> },
    { value: '8', label: 'Rainy / Thunderstorm', icon: <ThunderboltOutlined style={{ color: '#722ed1' }} /> },
    { value: '9', label: 'Thunderstorm', icon: <ThunderboltOutlined style={{ color: '#fa541c' }} /> },
    { value: '10', label: 'Snow / Ice', icon: <CloudFilled style={{ color: '#f0f0f0' }} /> }
];

const reportSections = [
    { key: 'technician', title: 'Technician Log', skipKey: 'isTechnicianLogSkipped' },
    { key: 'ehs', title: 'EHS Issues & Corrective Actions', skipKey: 'isEhsSkipped' },
    { key: 'equipment', title: 'Equipment Usage', skipKey: 'isEquipmentUsageSkipped' },
    { key: 'material', title: 'Material Consumption', skipKey: 'isMaterialUsageSkipped' },
    { key: 'consumables', title: 'Consumables & PPE Usage', skipKey: 'isConsumablesSkipped' },
    { key: 'downtime', title: 'Down Time', skipKey: 'isDowntimeSkipped' },
    { key: 'production', title: 'Production Joints', skipKey: 'isProductionJointSkipped' }
];

const DSRReportDetails = ({
    onSubmit,
    onSkip,
    onSectionSelect,
    activeShift,
    setActiveShift,
    submittedSections = {},
    skipped = { day: false, night: false, all: false },
    setSkipped,
    selectedProject,
    selectedMainline,
    selectedDate,
    isSubmitted: parentIsSubmitted,
    // Redux props
    onCreateDsrLog,
    onSubmitDsrLog,
    onUpdateDsrLogSections,
    isCreatingDsrLog,
    isSubmittingDsrLog,
    isUpdatingDsrLogSections,
    currentDsrLog,

}) => {
    // Add custom styles for red signature buttons and validation highlighting
    const customStyles = `
        <style>
            .red-signature-button:hover {
                border-color: #ff7875 !important;
                color: #ff7875 !important;
            }
            .red-signature-button:hover .anticon {
                color: #ff7875 !important;
            }
            .red-signature-button:focus {
                border-color: #ff7875 !important;
                color: #ff7875 !important;
                box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
            }
            .validation-error {
                border-color: #ff4d4f !important;
                box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
            }
            .validation-error:hover {
                border-color: #ff7875 !important;
            }
            .validation-error:focus {
                border-color: #ff4d4f !important;
                box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
            }
            .mandatory-field .ant-form-item-label > label::after {
                content: ' *';
                color: #ff4d4f;
            }
            .ant-btn:disabled {
                color: rgba(255, 255, 255, 0.85) !important;
                background-color: rgba(0, 0, 0, 0.04) !important;
                border-color: #d9d9d9 !important;
                text-shadow: none !important;
                box-shadow: none !important;
            }
            .ant-btn-primary:disabled {
                color: rgba(255, 255, 255, 0.85) !important;
                background-color: #ff4d4f !important;
                border-color: #ff4d4f !important;
                text-shadow: none !important;
                box-shadow: none !important;
            }
            .add-month-button:disabled {
                color: rgba(255, 255, 255, 0.85) !important;
                background-color: #ff4d4f !important;
                border-color: #ff4d4f !important;
                text-shadow: none !important;
                box-shadow: none !important;
            }
        </style>
    `;

    const [selectedDateValue, setSelectedDateValue] = useState(selectedDate ? moment(selectedDate) : moment());
    const [weather, setWeather] = useState();
    const [dayShiftWeather, setDayShiftWeather] = useState();
    const [nightShiftWeather, setNightShiftWeather] = useState();
    const [newWeather, setNewWeather] = useState('');
    const [form] = Form.useForm();
    const [signatureDrawerOpen, setSignatureDrawerOpen] = useState(false);
    const [signatureType, setSignatureType] = useState(null);
    const [signatureData, setSignatureData] = useState({});
    const [signatureSaveLoading, setSignatureSaveLoading] = useState(false);
    const [dayShiftComments, setDayShiftComments] = useState('');
    const [nightShiftComments, setNightShiftComments] = useState('');
    const [dsrLogId, setDsrLogId] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [updatingSection, setUpdatingSection] = useState(null);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [pdfUrl, setPdfUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [isReportGenerated, setIsReportGenerated] = useState(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);

    // Email modal state variables
    const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
    const [emailInput, setEmailInput] = useState('');
    const [emailList, setEmailList] = useState([]);
    const [isSharing, setIsSharing] = useState(false);

    // Use parent isSubmitted if available, otherwise use local state
    const finalIsSubmitted = parentIsSubmitted !== undefined ? parentIsSubmitted : isSubmitted;

    // Helper function to generate submission status fields for a specific section and shift
    const generateSubmissionStatus = (section, shift, isSubmitted = true) => {
        const shiftKeySuffix = shift === 'day' ? 'Dayshift' : 'Nightshift';
        let submittedKey;

        switch (section) {
            case 'technician':
                submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
                break;
            case 'downtime':
                submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
                break;
            case 'consumables':
                submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
                break;
            case 'equipment':
                submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'ehs':
                submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
                break;
            case 'material':
                submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'production':
                submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
                break;
            default:
                return {};
        }

        return { [submittedKey]: isSubmitted };
    };

    // Centralized skip handler for all sections - same logic as main DSR component
    const handleSectionSkipChange = (section, checked) => {
        if (!currentDsrLog?.id) return;

        // Set the updating section to show loading on this specific card
        setUpdatingSection(section);

        // Store the previous state for potential rollback
        const previousSectionsSkipped = { ...currentDsrLog.sectionsSkipped };

        // Optimistic update - update the state immediately
        const currentSectionsSkipped = currentDsrLog.sectionsSkipped || {};
        const shiftKeySuffix = activeShift === 'day' ? 'DayShift' : 'NightShift';

        let skipKey;
        switch (section) {
            case 'technician':
                skipKey = `isTechnicianLogSkipped${shiftKeySuffix}`;
                break;
            case 'downtime':
                skipKey = `isDowntimeSkipped${shiftKeySuffix}`;
                break;
            case 'consumables':
                skipKey = `isConsumablesSkipped${shiftKeySuffix}`;
                break;
            case 'equipment':
                skipKey = `isEquipmentUsageSkipped${shiftKeySuffix}`;
                break;
            case 'ehs':
                skipKey = `isEhsSkipped${shiftKeySuffix}`;
                break;
            case 'material':
                skipKey = `isMaterialUsageSkipped${shiftKeySuffix}`;
                break;
            case 'production':
                skipKey = `isProductionJointSkipped${shiftKeySuffix}`;
                break;
            default:
                setUpdatingSection(null);
                return;
        }

        // Optimistically update the currentDsrLog state
        const updatedSectionsSkipped = {
            ...currentSectionsSkipped,
            [skipKey]: checked ? 1 : 0
        };

        // Update the local state immediately for optimistic UI update
        if (currentDsrLog) {
            currentDsrLog.sectionsSkipped = updatedSectionsSkipped;
        }

        // Generate submission status - if section is being skipped, mark as submitted
        const submissionStatus = generateSubmissionStatus(section, activeShift, checked);

        // Call API in the background
        onUpdateDsrLogSections({
            dsrLogId: currentDsrLog.id,
            sectionsSkipped: updatedSectionsSkipped,
            submissionStatus: submissionStatus,
            previousState: previousSectionsSkipped // Store for potential rollback
        });
    };

    // Get section skip state - same logic as main DSR component
    const getSectionSkipState = (section) => {
        if (!currentDsrLog?.sectionsSkipped) return false;

        const shiftKeySuffix = activeShift === 'day' ? 'DayShift' : 'NightShift';
        let skipKey;

        switch (section) {
            case 'technician':
                skipKey = `isTechnicianLogSkipped${shiftKeySuffix}`;
                break;
            case 'downtime':
                skipKey = `isDowntimeSkipped${shiftKeySuffix}`;
                break;
            case 'consumables':
                skipKey = `isConsumablesSkipped${shiftKeySuffix}`;
                break;
            case 'equipment':
                skipKey = `isEquipmentUsageSkipped${shiftKeySuffix}`;
                break;
            case 'ehs':
                skipKey = `isEhsSkipped${shiftKeySuffix}`;
                break;
            case 'material':
                skipKey = `isMaterialUsageSkipped${shiftKeySuffix}`;
                break;
            case 'production':
                skipKey = `isProductionJointSkipped${shiftKeySuffix}`;
                break;
            default:
                return false;
        }

        return currentDsrLog.sectionsSkipped[skipKey] === 1;
    };

    // Get section submitted status from currentDsrLog API response
    const getSectionSubmittedState = (section) => {
        if (!currentDsrLog) return false;

        const shiftKeySuffix = activeShift === 'day' ? 'Dayshift' : 'Nightshift';
        let submittedKey;

        switch (section) {
            case 'technician':
                submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
                break;
            case 'downtime':
                submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
                break;
            case 'consumables':
                submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
                break;
            case 'equipment':
                submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'ehs':
                submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
                break;
            case 'material':
                submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'production':
                submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
                break;
            default:
                return false;
        }

        // API returns 1/0 (numbers), so check for truthy value (1) or boolean true
        return currentDsrLog[submittedKey] === 1 || currentDsrLog[submittedKey] === true;
    };

    // Clear updating section when Redux state changes
    useEffect(() => {
        if (!isUpdatingDsrLogSections && updatingSection) {
            setUpdatingSection(null);
        }
    }, [isUpdatingDsrLogSections, updatingSection]);

    // Force re-render when currentDsrLog sectionsSkipped changes
    useEffect(() => {
        if (currentDsrLog?.sectionsSkipped) {
    // Force re-render to update skip states
            setUpdatingSection(prev => prev);
        }
    }, [currentDsrLog?.sectionsSkipped]);

    // Update selectedDateValue when selectedDate prop changes
    useEffect(() => {
        if (selectedDate) {
            // Reset all form values before loading new data
            setSelectedDateValue(selectedDate);
            setWeather(null);
            setDayShiftWeather(null);
            setNightShiftWeather(null);
            setDayShiftComments('');
            setNightShiftComments('');
            setSignatureData({ manager: null, client: null });
            setDsrLogId(null);
            setIsSubmitted(false);
            setUpdatingSection(null);
            setValidationErrors({});
            setIsReportGenerated(false);
            setPdfUrl('');
            form.resetFields();
        }
    }, [selectedDate]);

    // Initialize DSR log when component mounts or dependencies change
    useEffect(() => {
        if (selectedProject && selectedMainline && selectedDateValue) {
            const logDate = selectedDateValue.format('YYYY-MM-DD');
            onCreateDsrLog({
                projectId: selectedProject.id,
                logDate: logDate,
                sowId: selectedMainline.id
            });
        }
    }, [selectedProject, selectedMainline, selectedDateValue]);

    // Update local state when DSR log is created
    useEffect(() => {
        if (currentDsrLog) {
            // Only update dsrLogId if it's not already set or if it's a different log
            if (!dsrLogId || dsrLogId !== currentDsrLog.id) {
                setDsrLogId(currentDsrLog.id);
            }

            // Check if DSR is already submitted
            if (currentDsrLog.logSubmitDate) {
                setIsSubmitted(true);
            } else {
                setIsSubmitted(false);
            }

            // Pre-populate form with existing data
            if (currentDsrLog.managerName) {
                form.setFieldsValue({
                    managerName: currentDsrLog.managerName,
                    clientName: currentDsrLog.clientName
                });
            }

            // Set existing weather
            if (currentDsrLog.weatherStatusDayshift) {
                setDayShiftWeather(currentDsrLog.weatherStatusDayshift.toString());
            }
            if (currentDsrLog.weatherStatusNightshift) {
                setNightShiftWeather(currentDsrLog.weatherStatusNightshift.toString());
            }
            // Set current weather based on active shift
            if (activeShift === 'day' && currentDsrLog.weatherStatusDayshift) {
                setWeather(currentDsrLog.weatherStatusDayshift.toString());
            } else if (activeShift === 'night' && currentDsrLog.weatherStatusNightshift) {
                setWeather(currentDsrLog.weatherStatusNightshift.toString());
            }

            // Set existing comments
            if (currentDsrLog.dsrDayShiftComments) {
                setDayShiftComments(currentDsrLog.dsrDayShiftComments);
            }
            if (currentDsrLog.dsrNightShiftComments) {
                setNightShiftComments(currentDsrLog.dsrNightShiftComments);
            }

            // Set skip states
            if (currentDsrLog.isSkippedDayShift !== undefined || currentDsrLog.isSkippedNightShift !== undefined) {
                setSkipped({
                    day: currentDsrLog.isSkippedDayShift || false,
                    night: currentDsrLog.isSkippedNightShift || false,
                    all: (currentDsrLog.isSkippedDayShift && currentDsrLog.isSkippedNightShift) || false
                });
            }

            // Set existing signatures if available (handle both base64 and S3 URLs)
            if (currentDsrLog.managerImageData) {
            // Base64 data
                setSignatureData(prev => ({ ...prev, manager: `data:image/png;base64,${currentDsrLog.managerImageData}` }));
            } else if (currentDsrLog.managerSignatureS3Object && currentDsrLog.managerSignatureS3Object.objectUrl) {
                // S3 URL
                setSignatureData(prev => ({ ...prev, manager: currentDsrLog.managerSignatureS3Object.objectUrl }));
            }

            if (currentDsrLog.clientImageData) {
            // Base64 data
                setSignatureData(prev => ({ ...prev, client: `data:image/png;base64,${currentDsrLog.clientImageData}` }));
            } else if (currentDsrLog.clientSignatureS3Object && currentDsrLog.clientSignatureS3Object.objectUrl) {
                // S3 URL
                setSignatureData(prev => ({ ...prev, client: currentDsrLog.clientSignatureS3Object.objectUrl }));
            }

            // Check if report is already generated
            if (currentDsrLog.reportDetails && currentDsrLog.reportDetails.fileUrl) {
                setIsReportGenerated(true);
                setPdfUrl(currentDsrLog.reportDetails.fileUrl);
            } else {
                setIsReportGenerated(false);
                setPdfUrl('');
            }
        }
    }, [currentDsrLog]);

    // Update weather display when active shift changes
    useEffect(() => {
        if (activeShift === 'day') {
            setWeather(dayShiftWeather);
        } else {
            setWeather(nightShiftWeather);
        }
    }, [activeShift, dayShiftWeather, nightShiftWeather]);

    const openSignaturePad = (type) => {
        setSignatureType(type);
        setSignatureDrawerOpen(true);
    };

    const closeSignaturePad = () => {
        setSignatureDrawerOpen(false);
        setSignatureType(null);
    };

    // Validation function to check all mandatory fields
    const validateForm = () => {
        const errors = {};

        // Check comments only for the currently active shift
        const currentComments = getCurrentComments();
        if (!currentComments || currentComments.trim() === '') {
            errors.comments = `Comments are required for ${activeShift === 'day' ? 'Day' : 'Night'} shift`;
        }
 // Check weather selection for the currently active shift
 const currentWeather = getCurrentWeather();
 if (!currentWeather) {
     errors.weather = `Weather selection is required for ${activeShift === 'day' ? 'Day' : 'Night'} shift`;
 }
        // Check manager name
        const managerName = form.getFieldValue('managerName');
        if (!managerName || managerName.trim() === '') {
            errors.managerName = 'Manager name is required';
        }

        // Check manager signature
        if (!signatureData.manager) {
            errors.managerSignature = 'Manager signature is required';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Add validation function to check if all mandatory fields are filled
    const isFormValid = () => {
        // Check if comments are filled for current active shift only
        const currentComments = getCurrentComments();
        if (!currentComments || currentComments.trim() === '') return false;

        const currentWeather = getCurrentWeather();
        if (!currentWeather) return false;
        // Check if manager name is filled
        const managerName = form.getFieldValue('managerName');
        if (!managerName || managerName.trim() === '') return false;

        // Check if manager signature is added
        if (!signatureData.manager) return false;

        return true;
    };

    const handleFinish = async (values) => {
        if (!dsrLogId) {
            message.error('DSR log not initialized. Please try again.');
            return;
        }

        // Validate form before submission
        if (!validateForm()) {
            // Create a detailed error message listing missing fields
            const missingFields = [];

            // Check comments for current shift
            const currentComments = getCurrentComments();
            if (!currentComments || currentComments.trim() === '') {
                missingFields.push(`${activeShift === 'day' ? 'Day' : 'Night'} Shift Comments`);
            }
            // Check weather for current shift
            const currentWeather = getCurrentWeather();
            if (!currentWeather) {
                missingFields.push(`${activeShift === 'day' ? 'Day' : 'Night'} Shift Weather`);
            }
            // Check manager name
            const managerName = form.getFieldValue('managerName');
            if (!managerName || managerName.trim() === '') {
                missingFields.push('Manager Name');
            }

            // Check manager signature
            if (!signatureData.manager) {
                missingFields.push('Manager Signature');
            }

            const errorMessage = `Please fill the following required fields: ${missingFields.join(', ')}`;
            message.error(errorMessage);
            return;
        }

        // Prepare the DSR log data according to the API structure
        const dsrLogData = {
            id: dsrLogId,
            sowId: selectedMainline.id,
            projectId: selectedProject.id,
            logCreatedBy: currentDsrLog.logCreatedBy,
            logDate: selectedDateValue.format('YYYY-MM-DD'),
            logSubmitDate: moment().toISOString(),
            weatherStatusDayshift: dayShiftWeather ? parseInt(dayShiftWeather) : null,
            weatherStatusNightshift: nightShiftWeather ? parseInt(nightShiftWeather) : null,
            managerImageData: signatureData.manager ? signatureData.manager.split(',')[1] : null,
            managerSignatureS3Object: signatureData.manager ? {
                contentType: "image/png",
                orginalFilename: `Manager_Signature_${Date.now()}.png`
            } : null,
            clientImageData: signatureData.client ? signatureData.client.split(',')[1] : null,
            clientSignatureS3Object: signatureData.client ? {
                contentType: "image/png",
                orginalFilename: `Client_Signature_${Date.now()}.png`
            } : null,
            managerName: values.managerName || '',
            clientName: values.clientName || '',
            dsrNightShiftComments: nightShiftComments,
            dsrDayShiftComments: dayShiftComments
        };

        try {
            // First submit the main DSR log
            await onSubmitDsrLog({ dsrLogData });

            // Then update the sectionsSkipped data using separate API
            const sectionsSkipped = {};
            const submissionStatus = {};

            reportSections.forEach(section => {
                const dayShiftKey = `${section.skipKey}DayShift`;
                const nightShiftKey = `${section.skipKey}NightShift`;

                // Get skip state for day shift
                const daySkipState = currentDsrLog.sectionsSkipped?.[dayShiftKey] === 1;
                sectionsSkipped[dayShiftKey] = daySkipState ? 1 : 0;

                // Get skip state for night shift
                const nightSkipState = currentDsrLog.sectionsSkipped?.[nightShiftKey] === 1;
                sectionsSkipped[nightShiftKey] = nightSkipState ? 1 : 0;

                // Generate submission status for both shifts
                const daySubmissionStatus = generateSubmissionStatus(section.key, 'day', daySkipState);
                const nightSubmissionStatus = generateSubmissionStatus(section.key, 'night', nightSkipState);

                Object.assign(submissionStatus, daySubmissionStatus, nightSubmissionStatus);
            });

            // Call the separate API for sectionsSkipped and submission status
            await onUpdateDsrLogSections({
                dsrLogId,
                sectionsSkipped,
                submissionStatus
            });

            message.success('DSR report submitted successfully!');
            setValidationErrors({});

        } catch (error) {
            console.error('Error submitting DSR report:', error);
            message.error('Failed to submit DSR report. Please try again.');
        }
    };

    const handleDSRSignatureSave = (base64Image) => {
        setSignatureData(prev => ({ ...prev, [signatureType]: base64Image }));
        // Clear validation error for this signature when added
        if (signatureType === 'manager' && validationErrors.managerSignature) {
            setValidationErrors(prev => ({ ...prev, managerSignature: undefined }));
        }
        closeSignaturePad();
    };

    const onWeatherChange = (value) => {
        if (activeShift === 'day') {
            setDayShiftWeather(value);
        } else {
            setNightShiftWeather(value);
        }
        setWeather(value);
    };

    const onWeatherSearch = (value) => {
        console.log('search:', value);
    };

    const onAddNewWeather = (e) => {
        setNewWeather(e.target.value);
    };

    const addWeatherItem = () => {
        if (newWeather) {
            weatherOptions.push({ value: newWeather, label: newWeather });
            setNewWeather('');
        }
    };

    const removeWeatherItem = () => {
        weatherOptions.pop();
    };

    const handleCommentsChange = (value) => {
        if (activeShift === 'day') {
            setDayShiftComments(value);
        } else {
            setNightShiftComments(value);
        }
        // Clear validation error when comments are entered
        if (validationErrors.comments) {
            setValidationErrors(prev => ({ ...prev, comments: undefined }));
        }
    };

    const getCurrentWeather = () => {
        return activeShift === 'day' ? dayShiftWeather : nightShiftWeather;
    };

    const getCurrentComments = () => {
        return activeShift === 'day' ? dayShiftComments : nightShiftComments;
    };

    const getWeatherLabel = (value) => {
        const option = weatherOptions.find(opt => opt.value === value);
        return option ? option.label : value;
    };

    const handleDateChange = (date) => {
        if (date) {
            setSelectedDateValue(date);
            if (onSubmit) onSubmit(date);
        }
    };

    // Check if a section is skipped for the current shift - simplified
    const isSectionSkipped = (sectionKey) => {
        return getSectionSkipState(sectionKey);
    };

    const handleGeneratePdf = async () => {
        if (!currentDsrLog?.id || !selectedProject?.id) {
            message.error('DSR log or project not available. Please try again.');
            return;
        }

        setIsGeneratingReport(true);
        try {
            const dsrLogId = currentDsrLog.id;
            const projectId = selectedProject.id;
            const url = `${process.env.REACT_APP_BASE_URL}/dsr-log/invoke-generate-report?dsrLogId=${dsrLogId}&projectId=${projectId}&generateFlashReport=1&generatePdfReport=1`;
            const authToken = await getJwtToken();

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': authToken,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to generate report');
            }

            const data = await response.json();
            setIsReportGenerated(true);
            message.success('Report generated successfully!');

            // If the API returns a PDF URL, show it in modal
            if (data.pdfUrl) {
                setPdfUrl(data.pdfUrl);
                setShowPdfModal(true);
            }
        } catch (err) {
            console.error('Error generating report:', err);
            message.error('Failed to generate report. Please try again.');
        } finally {
            setIsGeneratingReport(false);
        }
    };

    // Email modal functions
    const handleEmailInputChange = (e) => setEmailInput(e.target.value);

    const handleAddEmail = () => {
        const emails = emailInput.split(',').map(e => e.trim()).filter(e => e);
        setEmailList(prev => [...new Set([...prev, ...emails])]);
        setEmailInput('');
    };

    const handleRemoveEmail = (email) => {
        setEmailList(prev => prev.filter(e => e !== email));
    };

    const handleEmailModalOk = async () => {
        const authToken = await getJwtToken();
        if (emailList.length === 0) {
            message.warning('Please add at least one email address.');
            return;
        }

        setIsSharing(true);
        try {
            const dsrLogId = currentDsrLog?.id;
            const projectId = selectedProject?.id;
            const emailToDestinations = encodeURIComponent(emailList.join(','));
            const url = `${process.env.REACT_APP_BASE_URL}/dsr-log/invoke-generate-report?dsrLogId=${dsrLogId}&projectId=${projectId}&generateFlashReport=1&generatePdfReport=1&emailToDestinations=${emailToDestinations}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': authToken,
                },
            });

            if (!response.ok) throw new Error('Failed to generate and share report');

            message.success('Report generated and shared successfully!');
            setIsEmailModalVisible(false);
            setEmailList([]);
            setEmailInput('');
        } catch (err) {
            console.error('Error sharing report:', err);
            message.error('Failed to share report. Please try again.');
        } finally {
            setIsSharing(false);
        }
    };

    const handleEmailModalCancel = () => {
        setIsEmailModalVisible(false);
        setEmailList([]);
        setEmailInput('');
    };

    const handleGenerateReportClick = () => {
        setIsEmailModalVisible(true);
    };
    const handleGenerateFlashReportClick = async() => {
        try {
            const url = `${process.env.REACT_APP_BASE_URL}/dsr-log/generate-shift-flash-report`;
            const payload = {
             dsrLogId: currentDsrLog.id,
             shift: activeShift === 'day' ? 0 : 1,
            };

            // Get authentication token
            const authToken = await getJwtToken();

            const response = await fetch(url, {
             method: 'POST',
             headers: {
                 'Content-Type': 'application/json',
                 'Authorization': authToken,
             },
             body: JSON.stringify(payload),
            });
            
            if (!response.ok) {
                console.error('Error generating flash report:', error);
                message.error(`Failed to generate flash report: ${error.message}`);
            }
            
            const data = await response.json();
            message.success('Flash report generated successfully!');
        } catch (error) {
            console.error('Error generating flash report:', error);
            message.error(`Failed to generate flash report: ${error.message}`);
        }
     };

    return (
        <div className="dsr-report-details" style={{ width: '100%', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            {/* Inject custom styles */}
            <div dangerouslySetInnerHTML={{ __html: customStyles }} />

            {/* Read-only indicator */}
            {/* {finalIsSubmitted && (
                <div style={{
                    background: 'rgba(255, 77, 79, 0.1)',
                    border: '1px solid #ff4d4f',
                    borderRadius: 6,
                    padding: '12px 16px',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                }}>
                    <CheckCircleFilled style={{ color: '#ff4d4f', fontSize: 16 }} />
                    <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                        DSR Report has been submitted. This is a read-only view.
                    </span>
                </div>
            )} */}

            {/* Day/Night Shift Tabs using existing tab style */}
            <Tabs
                activeKey={activeShift}
                onChange={setActiveShift}
                style={{ marginBottom: 16 }}
                className="custom-tab"
                disabled={false}
            >
                <TabPane
                    tab="Day Shift"
                    key="day"
                    disabled={currentDsrLog?.isSkippedDayShift || false}
                />
                <TabPane
                    tab="Night Shift"
                    key="night"
                    disabled={currentDsrLog?.isSkippedNightShift || false}
                />
            </Tabs>

            {/* Weather Selection under tabs using existing dropdown pattern */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col>
                    <div>
                    <Select
                            className="weather-select"
                            placeholder='Select Weather'
                            showSearch
                            optionFilterProp="children"
                            onChange={(value) => {
                                onWeatherChange(value);
                                // Clear validation error when user selects weather
                                if (validationErrors.weather) {
                                    setValidationErrors(prev => ({ ...prev, weather: undefined }));
                                }
                            }}
                            value={getCurrentWeather()}
                            style={{ minWidth: 200 }}
                            suffixIcon={
                                getCurrentWeather() ? (
                                  <CloseOutlined
                                    style={{ color: 'white' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onWeatherChange(null); // clear selection manually
                                    }}
                                  />
                                ) : undefined
                              }
                            
                            dropdownRender={(menu) => (
                                <div>
                                    {menu}
                                </div>
                            )}
                            disabled={finalIsSubmitted || (activeShift === 'day' ? skipped.day : skipped.night)}
                        >
                            {weatherOptions.map((opt, i) => (
                                <Option key={i} value={opt.value}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {opt.icon}
                                        <span>{opt.label}</span>
                                    </div>
                                </Option>
                            ))}
                        </Select>
                        {validationErrors.weather && (
                            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                                {validationErrors.weather}
                            </div>
                        )}
                        *
                    </div>
                </Col>
            </Row>

            {/* Report Section Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {reportSections.map(section => {
                    const isSkipped = isSectionSkipped(section.key);
                    const isShiftSkipped = activeShift === 'day' ? skipped.day : skipped.night;
                    const isSectionDisabled = isShiftSkipped || finalIsSubmitted;
                    // Determine if section is submitted from API response
                    const isSectionSubmitted = getSectionSubmittedState(section.key);

                    return (
                        <Col xs={24} sm={12} md={8} lg={6} key={section.key}>
                            <Card
                                hoverable
                                className={''}
                                onClick={(e) => {
                                    console.log('Card clicked:', section.key);
                                    if (onSectionSelect) onSectionSelect(section.key);
                                }}
                                style={{
                                    border: activeShift === section.key ? '2px solid #1890ff' : undefined,
                                    position: 'relative',
                                    minHeight: 40,
                                    opacity: isSkipped || isShiftSkipped ? 0.6 : 1,
                                    backgroundColor: 'rgb(56, 31, 33)',
                                    borderRadius: 8,
                                    cursor: 'pointer'
                                }}
                            >
                                {/* Skip checkbox in top-left */}
                                {!finalIsSubmitted && (
                                    <div
                                        className="skip-checkbox-container"
                                        style={{
                                            position: 'absolute',
                                            top: 8,
                                            left: 8,
                                            zIndex: 2,
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: 6,
                                            margin: '4px'
                                        }}
                                    >
                                        <Checkbox
                                            checked={isSkipped}
                                            onChange={(e) => {
                                                console.log('Checkbox onChange triggered:', section.key, e.target.checked);
                                                handleSectionSkipChange(section.key, e.target.checked);
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                console.log('Checkbox onClick triggered:', section.key);
                                            }}
                                            // disabled={updatingSection === section.key || finalIsSubmitted || isShiftSkipped}
                                            className="red-checkbox"
                                            style={{
                                                transform: 'scale(1.2)'
                                            }}
                                        />
                                        {updatingSection === section.key && (
                                            <Spin
                                                indicator={<LoadingOutlined style={{ fontSize: 14, color: '#ff4d4f' }} spin />}
                                                size="small"
                                            />
                                        )}
                                        <span style={{
                                            fontSize: 14,
                                            color: '#8c8c8c',
                                            fontWeight: 500,
                                            textAlign: 'center',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6
                                        }}>
                                            Skip
                                        </span>
                                    </div>
                                )}

                                {/* Status icon in top right corner - only show for submitted sections */}
                                {isSectionSubmitted && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        zIndex: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '24px',
                                        height: '24px'
                                    }}>
                                        <CheckCircleFilled style={{ color: '#ff4d4f', fontSize: 20 }} />
                                    </div>
                                )}

                                <div style={{
                                    textAlign: 'center',
                                    fontWeight: 500,
                                    minHeight: 30,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: 50,
                                    paddingLeft: !finalIsSubmitted ? 60 : 0,
                                    paddingRight: isSectionSubmitted ? 40 : 0,
                                    gap: 4
                                }}>
                                    <div>{section.title}</div>

                                    {/* Skip status indicator in center - show for both section skip and shift skip */}
                                    {(isSkipped || isShiftSkipped) && (
                                        <div style={{
                                            fontSize: 12,
                                            color: '#ff4d4f',
                                            fontWeight: 500,
                                            backgroundColor: 'rgba(255, 77, 79, 0.1)',
                                            padding: '2px 8px',
                                            borderRadius: 4,
                                            border: '1px solid #ff4d4f',
                                            marginTop: 8
                                        }}>
                                            Skipped
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            {/* Global Form */}
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
            >
                <Form.Item
                    label={`Comments (${activeShift === 'day' ? 'Day' : 'Night'} Shift) *`}
                    className="mandatory-field"
                >
                    <TextArea
                        rows={3}
                        placeholder={`Enter ${activeShift} shift comments...`}
                        value={getCurrentComments()}
                        onChange={(e) => handleCommentsChange(e.target.value)}
                        disabled={finalIsSubmitted}
                        className={validationErrors.comments ? 'validation-error' : ''}
                    />
                    {validationErrors.comments && (
                        <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                            {validationErrors.comments}
                        </div>
                    )}
                </Form.Item>

                {/* Manager/Client Name Row */}
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Manager Name *"
                            name="managerName"
                            className="mandatory-field"
                        >
                            <Input
                                placeholder="Manager Name"
                                disabled={finalIsSubmitted}
                                style={{ height: '60px' }}
                                className={validationErrors.managerName ? 'validation-error' : ''}
                                onChange={(e) => {
                                    // Clear validation error when user starts typing
                                    if (validationErrors.managerName) {
                                        setValidationErrors(prev => ({ ...prev, managerName: undefined }));
                                    }
                                }}
                            />
                        </Form.Item>
                        {validationErrors.managerName && (
                            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: -16, marginBottom: 16 }}>
                                {validationErrors.managerName}
                            </div>
                        )}
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label="Client Name" name="clientName">
                            <Input
                                placeholder="Client Name"
                                disabled={finalIsSubmitted}
                                style={{ height: '60px' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Manager/Client Signature Row */}
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Manager Signature *"
                            name="managerSignature"
                            className="mandatory-field"
                        >
                            {!signatureData.manager ? (
                                <Button
                                    type="dashed"
                                    block
                                    style={{ height: '60px' }}
                                    icon={<FilePdfOutlined />}
                                    onClick={() => openSignaturePad('manager')}
                                    disabled={finalIsSubmitted}
                                    className={`red-signature-button ${validationErrors.managerSignature ? 'validation-error' : ''}`}
                                >
                                    Add Signature
                                </Button>
                            ) : (
                                <div
                                    style={{
                                        cursor: finalIsSubmitted ? 'default' : 'pointer',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: 8,
                                        padding: 8,
                                            opacity: finalIsSubmitted ? 0.7 : 1
                                        }}
                                        onClick={finalIsSubmitted ? undefined : () => openSignaturePad('manager')}
                                    >
                                        <img
                                            src={signatureData.manager}
                                            alt="Manager Signature"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                maxHeight: 100,
                                            objectFit: 'contain'
                                        }}
                                    />
                                </div>
                            )}
                        </Form.Item>
                        {validationErrors.managerSignature && (
                            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: -16, marginBottom: 16 }}>
                                {validationErrors.managerSignature}
                            </div>
                        )}
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label="Client Signature" name="clientSignature">
                            {!signatureData.client ? (
                                <Button
                                    type="dashed"
                                    style={{ height: '60px' }}
                                    block
                                    icon={<FilePdfOutlined />}
                                    onClick={() => openSignaturePad('client')}
                                    disabled={finalIsSubmitted}
                                    className="red-signature-button"
                                >
                                    Add Signature
                                </Button>
                            ) : (
                                <div
                                    style={{
                                        cursor: finalIsSubmitted ? 'default' : 'pointer',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: 8,
                                        padding: 8,
                                            opacity: finalIsSubmitted ? 0.7 : 1
                                        }}
                                        onClick={finalIsSubmitted ? undefined : () => openSignaturePad('client')}
                                    >
                                        <img
                                            src={signatureData.client}
                                            alt="Client Signature"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                maxHeight: 100,
                                            objectFit: 'contain'
                                        }}
                                    />
                                </div>
                            )}
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            {/* Fixed Submit/Cancel Buttons */}
            <div style={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1000,
                display: 'flex',
                gap: 12
            }}>
                <Button
                    onClick={() => {
                        form.resetFields();
                        setSignatureData({ manager: null, client: null });
                        setDayShiftComments('');
                        setNightShiftComments('');
                        setValidationErrors({});
                        if (onSkip) onSkip();
                    }}
                    size="large"
                    shape="round"
                >
                    Cancel
                </Button>

                {/* Generate Report Button */}


                {finalIsSubmitted ? (
                    <Button
                        type="primary"
                        size="large"
                        shape="round"
                        className="add-month-button"
                        disabled={true}
                        style={{
                            backgroundColor: '#f5f5f5',
                            borderColor: '#d9d9d9',
                            color: '#bfbfbf',
                            cursor: 'not-allowed',
                            opacity: 0.6
                        }}
                    >
                        Submitted
                    </Button>
                ) : (
                    <Button
                        type="primary"
                        onClick={() => form.submit()}
                        size="large"
                        shape="round"
                        className="add-month-button"
                        loading={isSubmittingDsrLog}
                        disabled={isCreatingDsrLog || !isFormValid()}
                        style={{
                            color: '#ffffff',
                            ...(isCreatingDsrLog || !isFormValid() ? {
                                backgroundColor: '#595959',
                                borderColor: '#595959',
                                color: '#bfbfbf',
                                cursor: 'not-allowed',
                                opacity: 0.6
                            } : {})
                        }}
                    >
                        Submit
                    </Button>
                )}
                 <Button
                    type="primary"
                    onClick={handleGenerateFlashReportClick}
                    size="large"
                    shape="round"
                    className="add-month-button"
                    loading={isGeneratingReport}
                    
                >
                    Generate Flash Report
                </Button>

                <Button
                    type="primary"
                    onClick={handleGenerateReportClick}
                    size="large"
                    shape="round"
                    className="add-month-button"
                    icon={<FilePdfOutlined />}
                    loading={isGeneratingReport}
                    disabled={!finalIsSubmitted || (activeShift === 'day' ? skipped.day : skipped.night) || isCreatingDsrLog || !currentDsrLog?.id}
                    style={{
                        color: '#ffffff',
                        ...(!finalIsSubmitted || (activeShift === 'day' ? skipped.day : skipped.night) || isCreatingDsrLog || !currentDsrLog?.id ? {
                            backgroundColor: '#595959',
                            borderColor: '#595959',
                            color: '#bfbfbf',
                            cursor: 'not-allowed',
                            opacity: 0.6
                        } : {})
                    }}
                >
                    Generate Report
                </Button>
            </div>

            <Drawer
                title="Signature"
                placement="right"
                onClose={closeSignaturePad}
                open={signatureDrawerOpen}
                width="32%"
                className="pipeline-modal Filter-Drawer signature-canvas"
            >
                <div className="drawer-content">
                    <Layout className="content-scrollable">
                        <Content>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', backgroundColor: 'transparent' }}>
                                <DSRSignaturePad
                                    visible={signatureDrawerOpen}
                                    onClose={closeSignaturePad}
                                    onSave={handleDSRSignatureSave}
                                    title={signatureType === 'manager' ? 'Manager' : 'Client'}
                                />
                            </div>
                        </Content>
                    </Layout>
                    <div className="ps-3 add-new-calibration-filter">
                        <Footer className="fixed-footer">
                            <div>
                                <Button type="primary" style={{ marginTop: 16, marginLeft: 10 }} onClick={closeSignaturePad}>
                                    Cancel
                                </Button>
                            </div>
                        </Footer>
                    </div>
                </div>
            </Drawer>

            <Modal
                visible={showPdfModal}
                onCancel={() => setShowPdfModal(false)}
                footer={null}
                width="80vw"
            >
                <iframe src={pdfUrl} style={{ width: '100%', height: '80vh', border: 'none' }} />
            </Modal>

            {/* Email Modal for Report Sharing */}
            <Modal
                title="Generate Report"
                visible={isEmailModalVisible}
                onOk={handleEmailModalOk}
                onCancel={handleEmailModalCancel}
                okText="Generate & Share"
                cancelText="Cancel"
                centered
                confirmLoading={isSharing}
                okButtonProps={{ disabled: emailList.length === 0 }}
            >
                <div>
                    <div style={{ marginBottom: 16 }}>
                        <label>Email</label>
                        <Input
                            placeholder="Enter emails followed by comma (,)"
                            value={emailInput}
                            onChange={handleEmailInputChange}
                            onPressEnter={handleAddEmail}
                            disabled={isSharing}
                        />
                        <Button
                            style={{ marginTop: 10, fontSize: 12 }}
                            type="primary"
                            size="large"
                            shape="round"
                            className="add-month-button"
                            onClick={handleAddEmail}
                            disabled={!emailInput.trim()}
                        >
                            Add
                        </Button>
                        <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                            eg: 1234@mail.com, 234@email.com
                        </div>
                        <div style={{ marginTop: 8 }}>
                            {emailList.map(email => (
                                <span key={email} style={{ marginRight: 8 }}>
                                    {email}
                                    <Button size="small" type="link" onClick={() => handleRemoveEmail(email)}>x</Button>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

const mapStateToProps = ({ dsrReducer }) => {
    const isCreatingDsrLog = get(dsrReducer, 'isCreatingDsrLog', false);
    const isSubmittingDsrLog = get(dsrReducer, 'isSubmittingDsrLog', false);
    const isUpdatingDsrLogSections = get(dsrReducer, 'isUpdatingDsrLogSections', false);
    const currentDsrLog = get(dsrReducer, 'currentDsrLog', null);

    return {
        isCreatingDsrLog,
        isSubmittingDsrLog,
        isUpdatingDsrLogSections,
        currentDsrLog
    };
};

const mapDispatchToProps = {
    onCreateDsrLog: createDsrLog,
    onSubmitDsrLog: submitDsrLog,
    onUpdateDsrLogSections: updateDsrLogSections
};

export default connect(mapStateToProps, mapDispatchToProps)(DSRReportDetails); 
